import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { projectService } from '../../services/projectService';
import { useContextStore } from '../../store/contextStore';
import { useNotificationStore } from '../../store/notificationStore';
import type { ProjectDto, CreateProjectRequest, UpdateProjectRequest } from '../../types/project';

interface ProjectFormProps {
  project: ProjectDto | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Project form component for creating and editing projects.
 */
export const ProjectForm = ({ project, open, onClose }: ProjectFormProps) => {
  const { currentTenant } = useContextStore();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotificationStore();

  useEffect(() => {
    if (project) {
      setName(project.name);
      setSlug(project.slug);
      setDescription(project.description || '');
      setIsActive(project.isActive);
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setIsActive(true);
    }
    setError(null);
  }, [project, open]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!project && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }
  }, [name, project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentTenant) {
      setError('Please select a tenant first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (project) {
        const updateData: UpdateProjectRequest = {
          name,
          slug,
          description: description || undefined,
          isActive,
        };
        await projectService.updateProject(project.id, currentTenant.id, updateData);
        showNotification('Project updated successfully', 'success');
      } else {
        const createData: CreateProjectRequest = {
          name,
          slug: slug || undefined,
          description: description || undefined,
        };
        await projectService.createProject(currentTenant.id, createData);
        showNotification('Project created successfully', 'success');
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save project');
      showNotification(err.response?.data?.message || 'Failed to save project', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!currentTenant) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Project</DialogTitle>
        <DialogContent>
          <Alert severity="warning">Please select a tenant first.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{project ? 'Edit Project' : 'Create Project'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              fullWidth
              helperText="URL-friendly identifier (auto-generated from name)"
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            {project && (
              <FormControlLabel
                control={
                  <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {project ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
